import { MaterialIcon, MaterialNameType } from "@website/icons/material";
import { USER_TRACKING_EVENTS } from "@website/tracking/tracking.types";
import { InfoChips } from "@website/ui/info-chips";
import { numberConvert } from "@website/utils/number-convert";
import {
  CheckboxItemEventTypes,
  FiltersSchemaType,
  useFilters
} from "@website/widget/filter";
import mapValues from "lodash-es/mapValues";
import toString from "lodash-es/toString";
import uniqueId from "lodash-es/uniqueId";
import { useRouter } from "next/router";
import { useMemo } from "react";
import { handleTrackingEvent } from "../map/index.tracking";
import { handleCategoryBadgeFilterClicked } from "./index.tracking";
import {
  HotelCategoryBadgeType,
  HotelCityAccommodations,
  HotelCityFacilities,
  HotelCityPriceRange,
  HotelCityRates,
  HotelCityStars
} from "./index.types";

const StarIcon = (star: number) => (
  <div className="flex">
    {Array.from({ length: star }, () => (
      <MaterialIcon
        className="text-on-surface-high-emphasis text-xs"
        key={uniqueId()}
        name="star"
      />
    ))}
  </div>
);

export const ratesInfoChips = {
  "4.5": {
    text: "عالی",
    color: "success"
  },
  "4": {
    text: "خیلی خوب",
    color: "success"
  },
  "3.5": {
    text: "خوب",
    color: "default"
  }
};

export const useHotelCitiesFilter = (
  categoryBadges?: HotelCategoryBadgeType,
  cityPriceRange?: HotelCityPriceRange,
  cityFacilities?: HotelCityFacilities,
  cityStars?: HotelCityStars,
  cityAccommodations?: HotelCityAccommodations,
  cityRates?: HotelCityRates,
  hotelListResultsCount?: number
) => {
  const router = useRouter();

  const onChangeCategoryBadge = (item: CheckboxItemEventTypes) => {
    handleCategoryBadgeFilterClicked({
      item_id: item.name,
      item: item.label,
      isChecked: item.isChecked,
      city: router?.query?.["cityTitle"],
      date_from: toString(router.query?.["date_from"]),
      date_to: toString(router.query?.["date_to"])
    });
  };

  const filtersSchema: FiltersSchemaType = useMemo(
    () => ({
      hotel_name: {
        type: "input",
        title: "جستجو بر اساس نام هتل"
      },
      discounted: {
        type: "switch",
        title: "اقامتگاه‌‌های دارای تخفیف"
      },
      price_range: {
        title: "قیمت",
        description: "مجموع قیمت",
        type: "range",
        min_key: "price_from",
        max_key: "price_to",
        step: 1,
        min: cityPriceRange?.min,
        max: cityPriceRange?.max
      },
      ...(cityStars.length > 0
        ? {
            stars: {
              type: "checkbox",
              title: "ستاره هتل",
              options: cityStars
                ?.filter((star) => star > 0)
                .map((star) => ({
                  name: `${star}`,
                  label: `${numberConvert(star, {
                    locale: router.locale
                  })} ستاره`,
                  icon: StarIcon(star)
                }))
                ?.sort((a, b) => Number(b?.name) - Number(a?.name))
            }
          }
        : {}),
      ...(categoryBadges?.length > 0 && {
        badge_categories: {
          type: "checkbox",
          title: "پیشنهادات ویژه",
          onChange: onChangeCategoryBadge,
          isNew: true,
          options: categoryBadges?.map((category) => ({
            name: String(category?.id),
            label: category?.name
          }))
        }
      }),
      ...(cityFacilities?.length > 0
        ? {
            facilities: {
              type: "checkbox",
              title: "امکانات هتل",
              isNew: true,
              collapsible: cityFacilities?.length > 5,
              options: cityFacilities
                ?.sort((a, b) => Number(a.id) - Number(b.id))
                ?.map((facility) => ({
                  name: String(facility?.id),
                  label: facility?.title,
                  icon: (
                    <MaterialIcon
                      className="text-on-surface-medium-emphasis text-headline-5"
                      name={
                        facility.icon === "what.ico"
                          ? "check_box"
                          : (facility.icon as MaterialNameType)
                      }
                      variant="outlined"
                    />
                  )
                }))
            }
          }
        : {}),
      ...(cityAccommodations.length > 0
        ? {
            accommodations: {
              type: "checkbox",
              title: "نوع اقامتگاه",
              hasSearch: true,
              collapsible: cityAccommodations.length > 5,
              options:
                cityAccommodations
                  ?.sort((a, b) => a.type.localeCompare(b.type))
                  ?.map((accommodation) => ({
                    name: accommodation.type,
                    label: accommodation.label
                  })) || []
            }
          }
        : {}),
      ...(cityRates.length > 0
        ? {
            rate: {
              type: "radio",
              title: "امتیاز کاربران",
              defaultValue: "all",
              isNew: true,
              items: [
                { value: "all", label: "همه" },
                ...(cityRates || []).map((rate) => ({
                  value: rate === 0 ? "new" : String(rate),
                  icon: ratesInfoChips[String(rate)] ? (
                    <InfoChips
                      StartIcon={
                        <MaterialIcon className="text-body-2" name="stars" />
                      }
                      color={ratesInfoChips[String(rate)]?.color || ""}
                      size="medium"
                      text={ratesInfoChips[String(rate)]?.text || ""}
                      variant="round-accent"
                    />
                  ) : undefined,
                  label:
                    rate === 0
                      ? "اقامتگاه‌های بدون امتیاز"
                      : `${numberConvert(rate, {
                          locale: router.locale
                        })}+ امتیاز`
                }))
              ]
            }
          }
        : {}),
      availability: {
        type: "switch",
        title: "حذف بدون ظرفیت",
        description: "فقط نمایش اقامتگاه های دارای ظرفیت"
      }
    }),
    [
      cityPriceRange?.min,
      cityPriceRange?.max,
      cityStars,
      cityFacilities,
      cityAccommodations,
      cityRates,
      router.locale
    ]
  );

  const chipsFiltersSchema: FiltersSchemaType = useMemo(
    () => ({
      ...(cityStars.length > 0
        ? {
            stars: {
              type: "checkbox",
              title: "ستاره هتل",
              options: cityStars
                ?.filter((star) => star > 0)
                .map((star) => ({
                  name: `${star}`,
                  label: `${numberConvert(star, {
                    locale: router.locale
                  })} ستاره`,
                  icon: StarIcon(star)
                }))
                ?.sort((a, b) => Number(b?.name) - Number(a?.name))
            }
          }
        : {}),
      ...(cityFacilities?.length > 0
        ? {
            facilities: {
              type: "checkbox",
              title: "امکانات هتل",
              isNew: true,
              collapsible: cityFacilities?.length > 5,
              options: cityFacilities
                ?.sort((a, b) => Number(a.id) - Number(b.id))
                ?.map((facility) => ({
                  name: String(facility?.id),
                  label: facility?.title,
                  icon: (
                    <MaterialIcon
                      className="text-on-surface-medium-emphasis text-headline-5"
                      name={
                        facility.icon === "what.ico"
                          ? "check_box"
                          : (facility.icon as MaterialNameType)
                      }
                      variant="outlined"
                    />
                  )
                }))
            }
          }
        : {}),
      ...(cityAccommodations.length > 0
        ? {
            accommodations: {
              type: "checkbox",
              title: "نوع اقامتگاه",
              hasSearch: true,
              collapsible: cityAccommodations.length > 5,
              options:
                cityAccommodations
                  ?.sort((a, b) => a.type.localeCompare(b.type))
                  ?.map((accommodation) => ({
                    name: accommodation.type,
                    label: accommodation.label
                  })) || []
            }
          }
        : {}),
      price_range: {
        title: "قیمت",
        description: "مجموع قیمت",
        type: "range",
        min_key: "price_from",
        max_key: "price_to",
        step: 1,
        min: cityPriceRange?.min,
        max: cityPriceRange?.max
      }
    }),
    [
      cityFacilities,
      cityAccommodations,
      cityPriceRange?.min,
      cityPriceRange?.max,
      cityAccommodations
    ]
  );

  const { clearFilters } = useFilters({ schema: filtersSchema });

  return {
    clearAllFilters: clearFilters,
    filtersSchema,
    trackingCallback: ({ removed }: { removed: object }) => {
      mapValues(removed, (value, key) =>
        handleTrackingEvent(USER_TRACKING_EVENTS.FILTER_PILL_REMOVED, {
          removed_filter_name: key,
          removed_filter_value: value
        })
      );
    },
    chipsFiltersSchema,
    hotelListResultsCount
  };
};
